import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

// AG product card. "+" adds a single-variant product straight to the cart;
// for a multi-variant product it opens the card's quick-view <dialog>, where
// option pickers resolve a variant and "Add to cart" adds it. Dispatches the
// same CartLinesUpdateEvent Horizon's forms use, so the cart drawer re-renders
// and auto-opens on its own.
class AgProductCard extends HTMLElement {
  #variants = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.dialog = this.querySelector('dialog.ag-qv');
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });
  }

  #onClick = (e) => {
    if (e.target.closest('[data-ag-close]')) { this.dialog?.close(); return; }
    if (e.target.closest('[data-ag-qv-add]')) {
      e.preventDefault();
      const v = this.#selectedVariant();
      if (v && v.available) this.#add(String(v.id), e.target.closest('button'));
      return;
    }
    const plus = e.target.closest('[data-ag-add]');
    if (!plus) return;
    e.preventDefault();
    if (this.dataset.single === 'true') {
      this.#add(this.dataset.variantId, plus);
    } else if (this.dialog) {
      this.#syncQuickView();
      this.dialog.showModal();
    }
  };

  #onChange = (e) => {
    if (e.target.matches('.ag-qv__value input')) this.#syncQuickView();
  };

  get variants() {
    if (!this.#variants) {
      const el = this.querySelector('[data-ag-variants]');
      this.#variants = el ? JSON.parse(el.textContent) : [];
    }
    return this.#variants;
  }

  #selectedOptions() {
    return [...this.querySelectorAll('[data-ag-option]')].map(
      (fs) => fs.querySelector('input:checked')?.value ?? null
    );
  }

  #selectedVariant() {
    const sel = this.#selectedOptions();
    return this.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
  }

  // Refresh price, image, availability and the "unavailable" state of each value.
  #syncQuickView() {
    const sel = this.#selectedOptions();
    const v = this.#selectedVariant();
    const price = this.querySelector('[data-ag-qv-price]');
    const add = this.querySelector('[data-ag-qv-add]');
    const note = this.querySelector('[data-ag-qv-note]');
    const img = this.querySelector('[data-ag-qv-img]');

    this.querySelectorAll('[data-ag-option]').forEach((fs, i) => {
      const label = fs.querySelector('[data-ag-qv-selected]');
      if (label) label.textContent = sel[i] ?? '';
      fs.querySelectorAll('.ag-qv__value').forEach((lab) => {
        const val = lab.querySelector('input').value;
        const others = sel.slice(); others[i] = val;
        const match = this.variants.find((x) => x.options.every((o, k) => o === others[k]));
        lab.classList.toggle('is-unavailable', !match || !match.available);
      });
    });

    if (v) {
      if (price) price.textContent = this.#money(v.price);
      if (img && v.featured_image?.src) img.src = v.featured_image.src;
      if (add) { add.disabled = !v.available; add.textContent = v.available ? 'Add to cart' : 'Sold out'; }
      note?.setAttribute('hidden', '');
    } else {
      if (add) { add.disabled = true; add.textContent = 'Unavailable'; }
      if (note) { note.textContent = 'That combination is not available.'; note.removeAttribute('hidden'); }
    }
  }

  #money(cents) {
    const fmt = this.dataset.moneyFormat || window.Theme?.moneyFormat || '£{{amount}}';
    return fmt.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  }

  async #add(variantId, button) {
    if (!variantId || this.hasAttribute('busy')) return;
    this.setAttribute('busy', '');
    button?.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId).filter(Boolean);
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      });
      const response = await res.json();
      if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');
      const ajaxCart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, source: 'ag-product-card', sourceId: this.dataset.productId,
          itemCount: 1, productId: this.dataset.productId, sections: response.sections, didError: false },
      });
      this.#status(`${this.dataset.title} added to cart`);
      this.dialog?.close();
      this.setAttribute('added', '');
      setTimeout(() => this.removeAttribute('added'), 1400);
    } catch (err) {
      this.dispatchEvent(new CartErrorEvent({ error: err.message, code: 'INVALID' }));
      deferred.reject(err);
      this.#status(err.message);
      const note = this.querySelector('[data-ag-qv-note]');
      if (note) { note.textContent = err.message; note.removeAttribute('hidden'); }
    } finally {
      this.removeAttribute('busy');
      button?.removeAttribute('aria-busy');
    }
  }

  async #refreshCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) { await customElements.whenDefined('cart-items-component'); return cartItems.fetchCartData(); }
    const r = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    return r.json();
  }

  #status(text) { const el = this.querySelector('.ag-card__status'); if (el) el.textContent = text; }
}

if (!customElements.get('ag-product-card')) customElements.define('ag-product-card', AgProductCard);
